// Copyright ©️ 2026 eVolve MEP, LLC

import type { MachineElement, WorkOrderWithStateChange } from '@evolvemep/api-ts';
import { List, Text } from '@mantine/core';

import type { ModalRegistryManagedProps } from 'components/Mantine/Modals/createModalRegistry';
import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { getDisplayNameWO } from 'modules/Shop/WorkOrders/WorkOrderDetail/common';

const WorkOrderStatusChangeItem = ({ workOrder }: { workOrder: WorkOrderWithStateChange }) => (
  <List.Item>
    <Text span fw={500}>
      {getDisplayNameWO(workOrder)}
    </Text>{' '}
    will move from {workOrder.workOrderStateName} to {workOrder.newWorkOrderStateName}.
  </List.Item>
);

type Props = ModalRegistryManagedProps & {
  onConfirm: () => Promise<void>;
  isDoAction: boolean;
  machineElements: MachineElement[];
  changingWOs: WorkOrderWithStateChange[];
};

/**
 * This modal tells the user their action on a fab item will also update the status of work orders and makes sure they
 * still want to continue.
 */
export const FabItemsWillUpdateWOModal = ({ onConfirm, isDoAction, changingWOs, ...props }: Props) => {
  const statusText = isDoAction ? 'done' : 'in progress';
  return (
    <WrappedModal
      title={`Mark as ${statusText}`}
      titleIcon="Alert"
      titleIconColor="yellow"
      onConfirm={onConfirm}
      confirmationText={`Mark as ${statusText}`}
      {...props}
    >
      <Text>Marking this work as {statusText} will also update the status of the following work orders:</Text>
      <List>
        {changingWOs.map((workOrder) => (
          <WorkOrderStatusChangeItem key={workOrder.workOrderId} workOrder={workOrder} />
        ))}
      </List>
      <Text>Do you want to continue?</Text>
    </WrappedModal>
  );
};
